<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import {useQuery} from "@tanstack/vue-query";
import axios from "axios";
import {computed, ref} from "vue";
import {useInfiniteScroll} from "@vueuse/core";
import CrawlPageRow from "@/components/actions/partials/settings/CrawlPageRow.vue";

const tableBody = ref(null);
const visibleRows = ref(50);

const orderBy = ref('decodedUrl');
const orderDirection = ref('asc');

const changeOrderBy = (name) => {
  if (orderBy.value === name) {
    orderDirection.value = orderDirection.value === 'asc' ? 'desc' : 'asc';
    return;
  }
  orderBy.value = name;
  orderDirection.value = 'desc';
};

const {data: pagesData, isLoading} = useQuery({
  queryKey: ['crawl-pages'],
  queryFn: async () => {
    const {data} = await axios.get('/ajax/v2/actions/crawl-page');

    return data.data;
  },
});

const pages = computed(() =>
  [...pagesData.value ?? []]
    .sort(function (a, b) {
      if (orderDirection.value === 'asc') {
        return a[orderBy.value] > b[orderBy.value] ? 1 : -1;
      }

      return a[orderBy.value] < b[orderBy.value] ? 1 : -1;
    })
    .slice(0, visibleRows.value)
);

useInfiniteScroll(
  computed(() => tableBody.value?.$el),
  () => {
    visibleRows.value += 50;
  },
  {
    distance: 200,
  }
)
</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container">
    <div class="tw-flex tw-flex-col tw-gap-y-8">
      <div class="tw-flex tw-flex-center tw-justify-between">
        <h1 class="tw-font-bold tw-text-4.5xl">
          Actions Settings
        </h1>
        <DefaultButton
          variant="primary"
          as="a"
          href="/actions/settings"
          class="tw-h-13"
        >
          <svg-vue
            icon="icons/back-arrow"
            class="tw-w-6 tw-h-6 tw-mr-3"
          />
          <span class="tw-font-normal">Back</span>
        </DefaultButton>
      </div>
      <div class="tw-w-full tw-flex tw-flex-col tw-gap-8">
        <div class="tw-bg-white tw-rounded-lg">
          <div class="tw-px-8 tw-py-4">
            <LoadingBlock v-if="isLoading" />

            <DataTable v-else>
              <TableHeaderRow>
                <TableHeaderCell
                  col-span="1"
                  :order-by="orderBy"
                  :order-direction="orderDirection"
                  name="status"
                  @order-changed="changeOrderBy"
                >
                  <span>Response</span>
                </TableHeaderCell>

                <TableHeaderCell
                  col-span="7"
                  :order-by="orderBy"
                  :order-direction="orderDirection"
                  name="decoded_url"
                  @order-changed="changeOrderBy"
                >
                  <span>Page</span>
                </TableHeaderCell>

                <TableHeaderCell
                  col-span="4"
                  name="tools"
                >
                  <span>Tools</span>
                </TableHeaderCell>
              </TableHeaderRow>

              <TableBody
                v-if="pages?.length > 0"
                ref="tableBody"
                class="tw-max-h-[70vh]"
              >
                <CrawlPageRow
                  v-for="page in pages"
                  :key="page.url"
                  :page="page"
                />
              </TableBody>

              <ZeroContent v-if="!pages || !pages.length">
                No pages found in the last crawl
              </ZeroContent>
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

